import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Truck, Tag, Building2, MapPin, Shield, Briefcase, Wrench,
  CreditCard, Award, Factory, Layers,
  Plus, Edit3, Trash2, X, Save, Search,
} from "lucide-react";

const ICON_MAP = {
  vehicle_types: Truck,
  vehicle_categories: Tag,
  vehicle_brands: Factory,
  vehicle_models: Layers,
  parking_spots: MapPin,
  insurance_types: Shield,
  insurance_companies: Building2,
  financing_companies: Briefcase,
  workshops: Wrench,
  fuel_card_providers: CreditCard,
  protection_companies: Award,
};

const FIELD_LABELS = {
  name: "Name",
  description: "Beschreibung",
  company: "Firmenname",
  contact_name: "Ansprechpartner",
  email: "E-Mail",
  phone: "Telefon",
  address: "Adresse",
  city: "Stadt",
  notes: "Notizen",
  brand_id: "Marke",
};

export default function FuhrparkMasterHub() {
  const [meta, setMeta] = useState({ lists: [] });
  const [activeKey, setActiveKey] = useState(null);

  useEffect(() => {
    api.get("/fuhrpark-master/_meta").then(({ data }) => {
      setMeta(data);
      if (data.lists?.length) setActiveKey(data.lists[0].key);
    }).catch(() => toast.error("Konnte Fuhrpark-Stammdaten nicht laden"));
  }, []);

  const activeSpec = meta.lists.find((l) => l.key === activeKey);

  return (
    <div className="space-y-3" data-testid="fuhrpark-master-hub">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-800 pb-2">
        {meta.lists.map((l) => {
          const Icon = ICON_MAP[l.key] || Tag;
          const active = activeKey === l.key;
          return (
            <button key={l.key} onClick={() => setActiveKey(l.key)}
              data-testid={`fuhrpark-subtab-${l.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors ${
                active
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {l.label}
            </button>
          );
        })}
      </div>
      {activeSpec && <ListManager spec={activeSpec} meta={meta} />}
    </div>
  );
}

function ListManager({ spec, meta }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [fkOptions, setFkOptions] = useState({});

  async function load() {
    try {
      const { data } = await api.get(`/fuhrpark-master/${spec.key}`,
        { params: search ? { q: search } : {} });
      setItems(data || []);
    } catch { toast.error("Laden fehlgeschlagen"); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [spec.key, search]);

  // Load FK options when relevant
  useEffect(() => {
    const fkFields = Object.entries(spec.fk || {});
    if (!fkFields.length) { setFkOptions({}); return; }
    Promise.all(fkFields.map(([fld, target]) =>
      api.get(`/fuhrpark-master/${target}`).then(({ data }) => [fld, data || []])
    )).then((entries) => setFkOptions(Object.fromEntries(entries)))
      .catch(() => {});
  }, [spec.key, spec.fk]);

  async function remove(it) {
    if (!window.confirm(`Eintrag „${it.name || it.company}" löschen?`)) return;
    try { await api.delete(`/fuhrpark-master/${spec.key}/${it.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  function newEmpty() {
    const empty = { __new: true };
    spec.fields.forEach((f) => { empty[f] = ""; });
    return empty;
  }

  // Determine which columns to show in table (max 4 visible columns)
  const tableCols = useMemo(() => {
    const cols = spec.fields.filter((f) => !["notes", "description"].includes(f) || spec.fields.length <= 2);
    return cols.slice(0, 5);
  }, [spec.fields]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid={`list-${spec.key}`}>
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="font-display font-semibold text-zinc-50 text-sm flex items-center gap-2">
            {spec.label}
          </h4>
          <p className="text-[11px] text-zinc-500 mt-0.5">{items.length} Einträge</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen…" data-testid={`search-${spec.key}`}
              className="bg-zinc-950 border border-zinc-800 rounded pl-7 pr-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-44" />
          </div>
          <button onClick={() => setEditing(newEmpty())}
            data-testid={`add-${spec.key}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Plus className="w-3.5 h-3.5" /> Neu hinzufügen
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-12 text-center text-zinc-500 text-sm">Keine Einträge.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                {tableCols.map((f) => (
                  <th key={f} className="px-4 py-3 text-left font-semibold">{FIELD_LABELS[f] || f}</th>
                ))}
                <th className="px-4 py-3 text-right font-semibold w-24">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                  data-testid={`row-${spec.key}-${it.id}`}>
                  {tableCols.map((f) => (
                    <td key={f} className="px-4 py-2.5 text-zinc-200">
                      {f === "brand_id"
                        ? (fkOptions.brand_id || []).find((b) => b.id === it[f])?.name || "—"
                        : (it[f] || "—")}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing({ ...it })}
                        data-testid={`edit-${spec.key}-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(it)}
                        data-testid={`delete-${spec.key}-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ItemModal initial={editing} spec={spec} fkOptions={fkOptions}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ItemModal({ initial, spec, fkOptions, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit() {
    for (const r of spec.required) {
      if (!form[r]?.toString().trim()) {
        toast.error(`Feld „${FIELD_LABELS[r] || r}" ist erforderlich`);
        return;
      }
    }
    setSaving(true);
    try {
      const data = {};
      spec.fields.forEach((f) => { if (form[f] !== undefined) data[f] = form[f]; });
      if (isNew) await api.post(`/fuhrpark-master/${spec.key}`, { data });
      else await api.put(`/fuhrpark-master/${spec.key}/${initial.id}`, { data });
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  function fieldInput(field) {
    const isLong = field === "notes";
    const isEmail = field === "email";
    const isPhone = field === "phone";
    const isFk = (spec.fk || {})[field];
    const required = spec.required.includes(field);
    const label = FIELD_LABELS[field] || field;

    if (isFk) {
      const opts = fkOptions[field] || [];
      return (
        <div key={field}>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
            {label}{required && <span className="text-red-400 ml-1">*</span>}
          </label>
          <select value={form[field] || ""} onChange={(e) => up(field, e.target.value)}
            data-testid={`field-${spec.key}-${field}`}
            className="w-full px-3 py-2 mt-1 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:outline-none focus:border-blue-500">
            <option value="">— Bitte wählen —</option>
            {opts.map((o) => <option key={o.id} value={o.id}>{o.name || o.company}</option>)}
          </select>
        </div>
      );
    }
    if (isLong) {
      return (
        <div key={field} className="col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
            {label}{required && <span className="text-red-400 ml-1">*</span>}
          </label>
          <textarea rows={3} value={form[field] || ""} onChange={(e) => up(field, e.target.value)}
            data-testid={`field-${spec.key}-${field}`}
            className="w-full px-3 py-2 mt-1 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:outline-none focus:border-blue-500" />
        </div>
      );
    }
    return (
      <div key={field}>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
          {label}{required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <input
          type={isEmail ? "email" : isPhone ? "tel" : "text"}
          value={form[field] || ""} onChange={(e) => up(field, e.target.value)}
          data-testid={`field-${spec.key}-${field}`}
          className="w-full px-3 py-2 mt-1 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:outline-none focus:border-blue-500" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl my-8 flex flex-col overflow-hidden max-h-[88vh]"
        onClick={(e) => e.stopPropagation()} data-testid={`modal-${spec.key}`}>
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 bg-zinc-900">
          <h3 className="font-display font-semibold text-zinc-100">
            {isNew ? `${spec.label}: Neuer Eintrag` : `${spec.label} bearbeiten`}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto">
          {spec.fields.map(fieldInput)}
        </div>
        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0 bg-zinc-900">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid={`save-${spec.key}`}
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : isNew ? "Anlegen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
